// File: frontend/src/components/pages/home/HowItWorks.js

import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { Upload, Brain, MessageCircle, FileText, Shield, CheckCircle, Car, Home, Heart, PawPrint, ArrowRight } from 'lucide-react';
import AuthModal from '../../auth/AuthModal';
import styles from './HowItWorks.module.css';

/* ============================================
   MOCKUP: Policies List
   ============================================ */
const MockupPoliciesList = ({ t }) => {
  const policies = [
    {
      name: 'TD Auto Insurance Policy',
      insurer: 'TD Insurance',
      number: 'TDI-2024-0847291',
      type: 'Auto',
      icon: Car,
      status: 'completed',
      date: 'Jan 15, 2025',
      size: '2.4 MB',
    },
    {
      name: 'Intact Home Protection Plan',
      insurer: 'Intact Insurance',
      number: 'INT-2024-1938475',
      type: 'Home',
      icon: Home,
      status: 'completed',
      date: 'Jan 12, 2025',
      size: '3.1 MB',
    },
    {
      name: 'Manulife Term Life Policy',
      insurer: 'Manulife Financial',
      number: 'MAN-2024-5728193',
      type: 'Life',
      icon: Heart,
      status: 'completed',
      date: 'Dec 28, 2024',
      size: '1.8 MB',
    },
    {
      name: 'Trupanion Pet Insurance',
      insurer: 'Trupanion',
      number: 'TRP-2025-0029384',
      type: 'Pet',
      icon: PawPrint,
      status: 'processing',
      date: 'Just now',
      size: '892 KB',
    },
  ];

  return (
    <div className={styles.mockup}>
      <div className={styles.mockupHeader}>
        <div className={styles.mockupHeaderLeft}>
          <span className={styles.mockupBackBtn}>←</span>
          <div>
            <h3 className={styles.mockupPageTitle}>{t('how_it_works.mockup1.title', 'My Policies')}</h3>
            <p className={styles.mockupPageSub}>{t('how_it_works.mockup1.subtitle', 'View and manage your uploaded insurance policies')}</p>
          </div>
        </div>
        <div className={styles.mockupUploadBtn}>
          <Upload size={12} strokeWidth={2.5} />
          <span>{t('how_it_works.mockup1.upload_btn', 'Upload Policy')}</span>
        </div>
      </div>

      <div className={styles.mockupPolicyList}>
        {policies.map((policy, i) => {
          const IconComp = policy.icon;
          return (
            <div key={i} className={styles.mockupPolicyCard}>
              <div className={styles.mockupPolicyIcon}>
                <IconComp size={18} strokeWidth={2} />
              </div>
              <div className={styles.mockupPolicyInfo}>
                <span className={styles.mockupPolicyName}>{policy.name}</span>
                <span className={styles.mockupPolicyMeta}>
                  {policy.insurer} · {policy.number}
                </span>
              </div>
              <div className={styles.mockupPolicyRight}>
                <span className={`${styles.mockupStatus} ${policy.status === 'completed' ? styles.mockupStatusDone : styles.mockupStatusProcessing}`}>
                  {policy.status === 'completed' ? (
                    <><CheckCircle size={10} strokeWidth={2.5} /> {t('how_it_works.mockup1.status_done', 'Completed')}</>
                  ) : (
                    <><span className={styles.mockupSpinner} /> {t('how_it_works.mockup1.status_processing', 'Processing')}</>
                  )}
                </span>
                <span className={styles.mockupPolicyDate}>{policy.date} · {policy.size}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================================
   MOCKUP: Policy Details
   ============================================ */
const MockupPolicyDetails = ({ t }) => {
  const fields = [
    { label: t('how_it_works.mockup2.field_company', 'Insurance Company'), value: 'TD Insurance' },
    { label: t('how_it_works.mockup2.field_number', 'Policy Number'), value: 'TDI-2024-0847291' },
    { label: t('how_it_works.mockup2.field_type', 'Policy Type'), value: 'Auto' },
    { label: t('how_it_works.mockup2.field_status', 'Policy Status'), value: 'Active', isActive: true },
    { label: t('how_it_works.mockup2.field_effective', 'Effective Date'), value: 'Jan 15, 2025' },
    { label: t('how_it_works.mockup2.field_expiry', 'Expiry Date'), value: 'Jan 15, 2026' },
  ];

  return (
    <div className={styles.mockup}>
      {/* Header with action buttons */}
      <div className={styles.mockupHeader}>
        <div className={styles.mockupHeaderLeft}>
          <span className={styles.mockupBackBtn}>←</span>
          <div>
            <h3 className={styles.mockupPageTitle}>{t('how_it_works.mockup2.title', 'TD Auto Insurance Policy')}</h3>
            <p className={styles.mockupPageSub}>{t('how_it_works.mockup2.subtitle', 'View extracted policy information')}</p>
          </div>
        </div>
        <div className={styles.mockupActionBtns}>
          <div className={styles.mockupActionBtn} data-color="blue">
            <MessageCircle size={10} strokeWidth={2.5} />
            <span>{t('how_it_works.mockup2.ask_btn', 'Ask About Policy')}</span>
          </div>
          <div className={styles.mockupActionBtn} data-color="green">
            <FileText size={10} strokeWidth={2.5} />
            <span>{t('how_it_works.mockup2.claim_btn', 'File a Claim')}</span>
          </div>
        </div>
      </div>

      {/* Success banner */}
      <div className={styles.mockupSuccessBanner}>
        <div className={styles.mockupSuccessIcon}>
          <CheckCircle size={20} strokeWidth={2} />
        </div>
        <div className={styles.mockupSuccessText}>
          <span className={styles.mockupSuccessTitle}>{t('how_it_works.mockup2.success_title', 'Policy Extracted Successfully!')}</span>
          <span className={styles.mockupSuccessSub}>{t('how_it_works.mockup2.success_sub', 'We\'ve analyzed your Auto Policy and extracted the key details.')}</span>
        </div>
        <div className={styles.mockupScores}>
          <div className={styles.mockupScore}>
            <span className={styles.mockupScoreLabel}>{t('how_it_works.mockup2.confidence', 'Extraction Confidence:')}</span>
            <span className={styles.mockupScoreValue} data-color="green">94%</span>
          </div>
          <div className={styles.mockupScore}>
            <span className={styles.mockupScoreLabel}>{t('how_it_works.mockup2.completeness', 'Data Completeness:')}</span>
            <span className={styles.mockupScoreValue} data-color="blue">87%</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.mockupTabs}>
        <span className={`${styles.mockupTab} ${styles.mockupTabActive}`}>{t('how_it_works.mockup2.tab_details', 'Policy Details')}</span>
        <span className={styles.mockupTab}>{t('how_it_works.mockup2.tab_coverage', 'Coverage')}</span>
        <span className={styles.mockupTab}>{t('how_it_works.mockup2.tab_additional', 'Additional Info')}</span>
      </div>

      {/* Data table */}
      <div className={styles.mockupTable}>
        <div className={styles.mockupTableHeader}>
          <Shield size={14} strokeWidth={2} />
          <span>{t('how_it_works.mockup2.table_title', 'Policy Information')}</span>
        </div>
        {fields.map((field, i) => (
          <div key={i} className={styles.mockupTableRow}>
            <span className={styles.mockupTableLabel}>{field.label}</span>
            <span className={`${styles.mockupTableValue} ${field.isActive ? styles.mockupTableValueActive : ''}`}>
              {field.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================================
   MOCKUP: Chat Assistant
   ============================================ */
const MockupChatAssistant = ({ t }) => {
  const suggestions = [
    t('how_it_works.mockup3.q1', 'What are my coverage limits?'),
    t('how_it_works.mockup3.q2', 'What\'s my deductible?'),
    t('how_it_works.mockup3.q3', 'What exclusions apply?'),
    t('how_it_works.mockup3.q4', 'When does my policy renew?'),
    t('how_it_works.mockup3.q5', 'Am I paying too much?'),
    t('how_it_works.mockup3.q6', 'What\'s covered under liability?'),
  ];

  return (
    <div className={styles.mockup}>
      {/* Chat header */}
      <div className={styles.mockupChatHeader}>
        <div className={styles.mockupChatAvatar}>
          <MessageCircle size={16} strokeWidth={2} />
        </div>
        <div className={styles.mockupChatHeaderText}>
          <span className={styles.mockupChatName}>{t('how_it_works.mockup3.name', 'Policy Assistant')}</span>
          <span className={styles.mockupChatStatus}>
            <span className={styles.mockupOnlineDot} />
            {t('how_it_works.mockup3.status', 'Connected')}
          </span>
        </div>
      </div>

      {/* Chat body */}
      <div className={styles.mockupChatBody}>
        {/* AI message */}
        <div className={styles.mockupChatBubble}>
          <p className={styles.mockupChatText}>
            {t('how_it_works.mockup3.ai_message', 'Hi! I\'ve analyzed your TD Auto Insurance policy and I\'m here to help you understand it better.')}
          </p>
          <p className={styles.mockupChatText}>
            {t('how_it_works.mockup3.ai_message2', 'I can answer questions about:')}
          </p>
          <ul className={styles.mockupChatList}>
            <li>{t('how_it_works.mockup3.topic1', 'Coverage details and limits')}</li>
            <li>{t('how_it_works.mockup3.topic2', 'Exclusions and restrictions')}</li>
            <li>{t('how_it_works.mockup3.topic3', 'Premium breakdown')}</li>
            <li>{t('how_it_works.mockup3.topic4', 'Deductibles and co-pays')}</li>
            <li>{t('how_it_works.mockup3.topic5', 'Renewal terms')}</li>
          </ul>
          <p className={styles.mockupChatText}>
            {t('how_it_works.mockup3.ai_prompt', 'What would you like to know?')}
          </p>
          <span className={styles.mockupChatTime}>11:13 AM</span>
        </div>

        {/* Suggestions */}
        <div className={styles.mockupSuggestions}>
          <span className={styles.mockupSuggestLabel}>{t('how_it_works.mockup3.try_asking', 'Try asking:')}</span>
          <div className={styles.mockupSuggestGrid}>
            {suggestions.map((q, i) => (
              <span key={i} className={styles.mockupSuggestChip}>{q}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Chat input */}
      <div className={styles.mockupChatInput}>
        <span className={styles.mockupChatPlaceholder}>{t('how_it_works.mockup3.input_placeholder', 'Ask me anything...')}</span>
        <div className={styles.mockupSendBtn}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
};

/* ============================================
   MAIN COMPONENT
   ============================================ */
const HowItWorks = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const STEPS = [
    {
      id: 'upload',
      icon: Upload,
      number: '01',
      title: t('how_it_works.step1.title', 'Upload Your Policy'),
      description: t('how_it_works.step1.description', 'Drop any insurance PDF — auto, home, health, life, pet, travel. Any insurer, any country. It takes 10 seconds.'),
      mockup: 'policies',
    },
    {
      id: 'analyze',
      icon: Brain,
      number: '02',
      title: t('how_it_works.step2.title', 'AI Reads Every Line'),
      description: t('how_it_works.step2.description', 'Our AI extracts coverage details, limits, deductibles, exclusions, and renewal dates — things you\'d never read yourself.'),
      mockup: 'details',
    },
    {
      id: 'ask',
      icon: MessageCircle,
      number: '03',
      title: t('how_it_works.step3.title', 'Ask Anything'),
      description: t('how_it_works.step3.description', 'Coverage gaps? Claim questions? "Am I paying too much?" Just ask. Get straight answers from your own policy data — no jargon.'),
      mockup: 'chat',
    },
  ];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleScroll = () => {
      const scrollLeft = track.scrollLeft;
      const cardWidth = track.children[0]?.offsetWidth || track.offsetWidth * 0.85;
      const gap = 24;
      const newActive = Math.round(scrollLeft / (cardWidth + gap));
      setActiveStep(Math.min(Math.max(newActive, 0), STEPS.length - 1));
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => track.removeEventListener('scroll', handleScroll);
  }, [STEPS.length]);

  const scrollToStep = (index) => {
    setActiveStep(index);
    if (trackRef.current && trackRef.current.children[index]) {
      const card = trackRef.current.children[index];
      const gap = 24;
      trackRef.current.scrollTo({
        left: (card.offsetWidth + gap) * index,
        behavior: 'smooth',
      });
    }
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  const renderMockup = (type) => {
    switch (type) {
      case 'policies': return <MockupPoliciesList t={t} />;
      case 'details': return <MockupPolicyDetails t={t} />;
      case 'chat': return <MockupChatAssistant t={t} />;
      default: return null;
    }
  };

  return (
    <section id="how-it-works" className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.label}>{t('how_it_works.label', 'How It Works')}</span>
          <h2 className={styles.title}>{t('how_it_works.title', 'Three steps to insurance clarity')}</h2>
          <p className={styles.subtitle}>
            <span className={styles.subtitleHighlights}>
              <span className={styles.subtitleChip}>No forms.</span>
              <span className={styles.subtitleChip}>No phone calls.</span>
              <span className={styles.subtitleChip}>No waiting.</span>
            </span>
            <span className={styles.subtitleText}>
              Upload a PDF and know everything in minutes.
            </span>
          </p>
        </div>

        {/* Progress tabs */}
        <div className={styles.progress}>
          {STEPS.map((step, i) => (
            <button
              key={step.id}
              className={`${styles.progressStep} ${i === activeStep ? styles.progressStepActive : ''} ${i < activeStep ? styles.progressStepDone : ''}`}
              onClick={() => scrollToStep(i)}
            >
              <span className={styles.progressNumber}>{step.number}</span>
              <span className={styles.progressLabel}>{step.title}</span>
            </button>
          ))}
          <div
            className={styles.progressBar}
            style={{ width: `${((activeStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Horizontal scrolling cards */}
        <div className={styles.track} ref={trackRef}>
          {STEPS.map((step) => {
            const IconComponent = step.icon;
            return (
              <div key={step.id} className={styles.card}>
                <div className={styles.cardText}>
                  <div className={styles.cardNumber}>{step.number}</div>
                  <div className={styles.cardIcon}>
                    <IconComponent size={24} strokeWidth={2} />
                  </div>
                  <h3 className={styles.cardTitle}>{step.title}</h3>
                  <p className={styles.cardDescription}>{step.description}</p>
                  
                  {/* CTA button - hidden on mobile, shown on tablet+ */}
                  <button
                    className={`${styles.cardCta} ${styles.cardCtaDesktop}`}
                    onClick={handleGetStarted}
                  >
                    <span>{t('how_it_works.cta', 'Get Started Free')}</span>
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                </div>

                <div className={styles.cardMockup}>
                  <div className={styles.deviceFrame}>
                    <div className={styles.deviceBar}>
                      <div className={styles.deviceDots}>
                        <span /><span /><span />
                      </div>
                      <div className={styles.deviceUrl}>allegoryinsurance.com</div>
                      <div className={styles.deviceDotsSpacer} />
                    </div>
                    <div className={styles.deviceScreen}>
                      {renderMockup(step.mockup)}
                    </div>
                  </div>
                  
                  {/* CTA button - shown on mobile, hidden on tablet+ */}
                  <button
                    className={`${styles.cardCta} ${styles.cardCtaMobile}`}
                    onClick={handleGetStarted}
                  >
                    <span>{t('how_it_works.cta', 'Get Started Free')}</span>
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile step dots */}
        <div className={styles.dots}>
          {STEPS.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === activeStep ? styles.dotActive : ''}`}
              onClick={() => scrollToStep(i)}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </section>
  );
};

export default HowItWorks;